// Chat Message Component
// Displays individual messages in chatbot-style interface
// ChickyTutor-style: Mascot on left for AI, user avatar on right

import React, { memo } from 'react';
import './ChatMessage.css';

function renderCoachReply(text: string): React.ReactNode {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <strong
            key={i}
            style={{
              color: 'var(--color-blue)',
              fontWeight: 700
            }}
          >
            {part}
          </strong>
        ) : (
          part
        )
      )}
    </>
  );
}

export interface ChatMessageProps {
  role: 'coach' | 'user' | 'system';
  content: string | React.ReactNode;
  timestamp?: Date;
  showTimestamp?: boolean;
  imageUrl?: string;
  options?: Array<{ id: string; text: string; letter: string }>;
  onOptionClick?: (optionId: string) => void;
  selectedOption?: string | null;
  correctAnswerId?: string;
  isAnswered?: boolean;
  className?: string;
  userName?: string;
  messageId?: string; // Add message ID for scrolling to specific messages
}

export const ChatMessage: React.FC<ChatMessageProps> = memo(({
  role,
  content,
  timestamp,
  showTimestamp = false,
  imageUrl,
  options,
  onOptionClick,
  selectedOption,
  correctAnswerId,
  isAnswered = false,
  className,
  userName: _userName,
  messageId
}) => {
  const roleClass = role === 'user' ? 'user' : role === 'system' ? 'system' : 'ai';

  return (
    <div 
      id={messageId} 
      className={`chat-message chat-message--no-avatars ${roleClass} ${className || ''}`}
    >
      <div className="chat-message-content">
        {/* Labels removed - cleaner UI, context is clear from avatars and styling */}
        {showTimestamp && timestamp && (
          <div className="chat-message-header">
            <span className="chat-message-time">
              {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        )}

        <div className="chat-message-body">
          {typeof content === 'string' ? (
            content.trim().length > 0 ? (
              <div className="chat-message-text">
                {renderCoachReply(content)}
              </div>
            ) : null
          ) : (
            content
          )}
          
          {imageUrl && (
            <div className="chat-message-image">
              <img
                src={imageUrl}
                alt="Question"
                loading="eager"
                decoding="sync"
                fetchPriority="high"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) parent.style.display = 'none';
                }}
              />
            </div>
          )}
          
          {options && options.length > 0 && (
            <div className="chat-message-options">
              {options.map((option) => {
                const isSelected = selectedOption === option.id;
                const isCorrect = isAnswered && option.id === correctAnswerId;
                const isIncorrect = isAnswered && isSelected && option.id !== correctAnswerId;
                
                return (
                  <button
                    key={option.id}
                    className={`chat-option-btn ${isSelected ? 'selected' : ''} ${isCorrect ? 'correct' : ''} ${isIncorrect ? 'incorrect' : ''}`}
                    onClick={() => !isAnswered && onOptionClick?.(option.id)}
                    disabled={isAnswered}
                  >
                    <span className="chat-option-letter">{option.letter}</span>
                    <span className="chat-option-text">{option.text}</span>
                  </button>
                );
              })}
            </div>
          )}
          

        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if relevant props change
  // Prevent flickering by avoiding unnecessary re-renders
  if (prevProps.role !== nextProps.role) return false;
  if (prevProps.content !== nextProps.content) return false;
  if (prevProps.imageUrl !== nextProps.imageUrl) return false;
  if (prevProps.selectedOption !== nextProps.selectedOption) return false;
  if (prevProps.isAnswered !== nextProps.isAnswered) return false;
  if (prevProps.correctAnswerId !== nextProps.correctAnswerId) return false;
  if (prevProps.onOptionClick !== nextProps.onOptionClick) return false;
  if (prevProps.messageId !== nextProps.messageId) return false;
  
  // Compare options array
  if (prevProps.options?.length !== nextProps.options?.length) return false;
  if (prevProps.options && nextProps.options) {
    for (let i = 0; i < prevProps.options.length; i++) {
      if (prevProps.options[i].id !== nextProps.options[i].id ||
          prevProps.options[i].text !== nextProps.options[i].text ||
          prevProps.options[i].letter !== nextProps.options[i].letter) {
        return false;
      }
    }
  }
  
  // Props are equal, skip re-render
  return true;
});

ChatMessage.displayName = 'ChatMessage';


